import React, { createContext, useState, useRef, ReactNode, useCallback, Dispatch, SetStateAction } from 'react';
import { ChatsService } from '../../services/Chats/ChatsService';
import { useLocation } from 'react-router-dom';
import { ToastSettings } from '../../utils/ToastSettings';
import { ChatsStatusEnum } from '../../enums/Chat/ChatStatusEnum';
import { ChatMessageModel, FileAttachment } from '../../models/Chat/ChatMessageModel';
import { ChatModel } from '../../models/Chat/ChatModel';

export type StateType = "default" | "started" | "tutorial";

// ---------------------------------------------------------------------------
// AI-947 — cancelamento otimista do formulário ao mudar de assunto.
//
// O formulário (quiz) é respondido pelo WIDGET, que envia um resumo markdown
// "**pergunta** resposta" (ver ChatQuiz.submitAll). Qualquer mensagem digitada na
// BARRA do chat (sem esse formato) significa que o parceiro abandonou o formulário.
// Nesse caso o backend descarta o refinamento e troca o box por um aviso de
// cancelamento (ver _dismiss_active_quiz no orchestrator); aqui replicamos o efeito
// de forma OTIMISTA (antes da resposta da API) para não exibir o flash da mensagem
// dentro do box do formulário.
//
// A regra é a MESMA do backend (formato da mensagem, sem heurística de frases), então
// o estado otimista sempre bate com o que a API devolve — o formulário não "ressuscita"
// quando a resposta chega. O cabeçalho ("[SIAPE]") NÃO é hardcodado: é lido do próprio
// formulário ativo (o identificador do agente da última resposta), funcionando para
// qualquer agente.
// ---------------------------------------------------------------------------

// Texto do aviso (espelha _QUIZ_CANCELLED_MESSAGE no backend).
const QUIZ_CANCELLED_TEXT =
  "Envio das respostas cancelado. Você iniciou uma nova consulta antes de concluir o formulário.";

// Resposta enviada PELO formulário (widget) -> resumo "**pergunta** resposta".
// Qualquer outra coisa veio da barra do chat.
const isFormAnswerSubmission = (msg: string): boolean => /^\s*\*\*/.test(msg ?? "");

// Extrai o cabeçalho de categoria ("[SIAPE]" ou "<h3>SIAPE</h3>") do início do
// conteúdo — é o identificador do agente ativo, reaproveitado no aviso.
const CATEGORY_HEADER_RE = /^\s*(\[[^\]\n]+\]|<h3>.*?<\/h3>)/;

const extractCategoryHeader = (content?: string | null): string | null =>
  content?.match(CATEGORY_HEADER_RE)?.[1] ?? null;

// Encerra (clear quiz + aviso) o formulário ATIVO, se houver. O formulário ativo é
// sempre a ÚLTIMA resposta do agente; mensagens de refinamento mais antigas mantêm o
// quiz salvo mas não estão mais em aberto. Mantém dinamicamente o cabeçalho do agente
// dessa última resposta (espelha _dismiss_active_quiz no backend).
const cancelOpenQuiz = (messages: ChatMessageModel[]): ChatMessageModel[] => {
  let lastAgentIdx = -1;
  messages.forEach((m, i) => {
    if (m?.sender === "agent") lastAgentIdx = i;
  });
  if (lastAgentIdx < 0 || !messages[lastAgentIdx]?.quiz) return messages;
  return messages.map((m, i) => {
    if (i !== lastAgentIdx) return m;
    const header = extractCategoryHeader(m?.content) ?? "";
    const content = header ? `${header}\n${QUIZ_CANCELLED_TEXT}` : QUIZ_CANCELLED_TEXT;
    return { ...m, quiz: null, content };
  });
};

interface ContextProps {
    message: string;
    setMessage: Dispatch<SetStateAction<string>>,
    chat: ChatModel | null;
    setChat: Dispatch<SetStateAction<ChatModel | null>>,
    chats: ChatModel[];
    isStreaming: boolean;
    setIsStreaming: Dispatch<SetStateAction<boolean>>,
    isCancelling: boolean;
    isLoadingVote: boolean;
    isVoting: boolean;
    agentFinalText: string;
    state: StateType;
    setState: (state: StateType) => void;
    handleSend: (message: string, assistantId?: number | null, files?: File[]) => void;
    handleStop: () => void;
    handleList: () => void;
    handleMe: () => void;
    handleVote: (messageId: number, vote: boolean) => void;
    showSimulation: boolean;
    setShowSimulation: Dispatch<SetStateAction<boolean>>;
    activeAssistantId: number | null;
    setActiveAssistantId: Dispatch<SetStateAction<number | null>>;
    isLoadingMessages: boolean;
    handleSelectAssistant: (id: number | null) => Promise<ChatModel | null>;
}

export const ChatsContext = createContext<ContextProps>({} as ContextProps);

export const ChatsProvider: React.FC<{ children: ReactNode }> = (props: any) => {
    const [chats, setChats] = useState<ChatModel[]>([]);
    const [chat, setChat] = useState<ChatModel | null>(null);
    const [, setIsLoading] = useState<boolean>(true);
    const [isLoadingVote, setIsLoadingVote] = useState<boolean>(false);
    const [, setError] = useState<string>('');
    const [params,] = useState<any>({ page: 1 });
    const [message, setMessage] = useState<string>('');
    const [isStreaming, setIsStreaming] = useState(false);
    // AI-940: geração sendo interrompida (entre o clique em parar e a chegada do marcador).
    const [isCancelling, setIsCancelling] = useState(false);
    const [showSimulation, setShowSimulation] = useState(false);
    const [isVoting, setIsVoting] = useState(false);
    const [agentFinalText,] = useState<string>("");
    const [state, setState] = useState<StateType>('started');
    const [activeAssistantId, setActiveAssistantId] = useState<number | null>(null);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const messageDraftsRef = useRef<Record<string, string>>({});
    // AI-940: id da geração em andamento (para o POST /chats/cancel).
    const generationIdRef = useRef<string | null>(null);

    const service = new ChatsService();
    const location = useLocation();

    const handleSelectAssistant = async (id: number | null): Promise<ChatModel | null> => {
        if (activeAssistantId !== null) {
            messageDraftsRef.current[String(activeAssistantId)] = message;
        }

        setActiveAssistantId(id);
        setMessage(id !== null ? (messageDraftsRef.current[String(id)] ?? '') : '');

        if (id === null) {
            setChat(null);
            return null;
        }

        try {
            setState('started');
            setIsLoadingMessages(true);
            setChat(null);

            const [_Response, _Error] = await service.me(params, { assistant_id: id });

            if (!!_Error) return null;

            const data = _Response?.data as ChatModel[] | undefined;

            if (!data?.length) {
                setChat(null);
                setState('default');
                return null;
            }

            const activeChatMeta = data.find((x) => x.status?.id === ChatsStatusEnum.EM_ANDAMENTO) || data[0];
            const sortedMessages = data
                .flatMap((c) => c.messages ?? [])
                .sort((a, b) => new Date(a.created_at ?? 0).getTime() - new Date(b.created_at ?? 0).getTime());

            // Ao carregar o histórico (reload / troca de assistente) as mensagens são
            // exibidas como estão. O streaming só deve ser acionado quando chega uma
            // resposta nova da API (em handleSend), nunca ao recarregar a página.
            const loadedChat: ChatModel = { ...activeChatMeta, messages: sortedMessages };

            setChat(loadedChat);
            setState(sortedMessages.length ? 'started' : 'default');

            return { ...activeChatMeta, messages: sortedMessages };
        } catch (e) {
            return null;
        } finally {
            setIsLoadingMessages(false);
        }
    };

    const handleSend = async (message: string, assistantId?: number | null, files?: File[]) => {
        try {
            const targetAssistantId = assistantId !== undefined ? assistantId : activeAssistantId;

            if (!message?.trim() && (!files || files.length === 0)) {
                return;
            }

            if (targetAssistantId === null) {
                ToastSettings("Selecione um assistente para continuar.", "bottom-center", "error", () => { });
                return false;
            }

            setIsVoting(false);
            setMessage("");

            const createdAt = new Date().toISOString();
            const tempFileId = Math.floor(Math.random() * -1000000);

            const tempFiles: FileAttachment[] = files?.map((file, idx) => ({
                id: tempFileId - idx,
                filename: file.name,
                url: file.type.startsWith('image/') ? URL.createObjectURL(file) : file.name,
            })) ?? [];

            const userMessage: ChatMessageModel = {
                content: message,
                sender: "user",
                created_at: createdAt,
                is_remove: true,
                assistant_id: targetAssistantId,
                files: tempFiles,
            };

            const agentPlaceholder: ChatMessageModel = {
                content: "",
                sender: "agent",
                created_at: createdAt,
                is_remove: true,
                assistant_id: targetAssistantId,
            };

            setChat((prev) => {
                const baseMessages = prev?.messages?.filter((m) => !m.is_remove) ?? [];

                // AI-947: mensagem da barra (não é resumo do formulário) com formulário
                // aberto -> cancela o formulário de forma otimista, igual ao backend,
                // evitando o flash da mensagem dentro do box do formulário.
                const optimisticBase = !isFormAnswerSubmission(message)
                    ? cancelOpenQuiz(baseMessages)
                    : baseMessages;

                return {
                    ...(prev ?? {
                        chat_id: 0,
                        status: { id: 1, name: "Em andamento" },
                        created_at: new Date().toISOString(),
                    }),
                    messages: [...optimisticBase, userMessage, agentPlaceholder],
                };
            });

            setState('started');
            setIsStreaming(true);
            setIsCancelling(false);

            // AI-940: id da geração para permitir o cancelamento (POST /chats/cancel).
            // NÃO abortamos a requisição: ao cancelar, o backend devolve o marcador
            // já salvo (com id), que segue o fluxo normal de resposta abaixo —
            // ganhando a animação de digitação e os botões de feedback.
            const generationId =
                (typeof crypto !== "undefined" && "randomUUID" in crypto)
                    ? crypto.randomUUID()
                    : `gen-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            generationIdRef.current = generationId;

            let sendResponse;
            if (files && files.length > 0) {
                const formData = new FormData();
                formData.append('content', message);
                formData.append('assistant_id', String(targetAssistantId));
                formData.append('generation_id', generationId);
                files.forEach((file) => {
                    formData.append('files', file);
                });
                sendResponse = await service.sendWithFiles(formData);
            } else {
                sendResponse = await service.send(
                    { content: message, assistant_id: targetAssistantId, generation_id: generationId },
                );
            }

            // AI-940: se uma nova geração começou enquanto esta aguardava (ex.: o
            // usuário parou e enviou outra), ignora esta resposta para não sobrescrever
            // a UI da geração mais nova.
            if (generationIdRef.current !== generationId) {
                return false;
            }
            // Resposta desta geração chegou -> encerra o loader de cancelamento (se houver).
            setIsCancelling(false);

            const [_Response, _Error, _Code, _Errors] = sendResponse;

            if (!!_Error) {
                ToastSettings(_Response?.message || _Error, "bottom-center", "error", () => { }, _Errors);
                setIsStreaming(false);
                return false;
            }

            const fullChat = _Response.data as ChatModel;

            const apiMessages = fullChat.messages ?? [];

            const lastRealAgentMessage = [...apiMessages]
                .reverse()
                .find((m) => m.sender === "agent" && m.content?.trim() !== "");

            if (!lastRealAgentMessage) {
                setChat({
                    ...fullChat,
                    messages: apiMessages,
                });
                setIsStreaming(false);
                return true;
            }

            const messagesWithoutLastAgent = apiMessages.filter(
                (m) =>
                    !(
                        m.sender === "agent" &&
                        m.content === lastRealAgentMessage.content &&
                        m.created_at === lastRealAgentMessage.created_at
                    )
            );

            const updatedMessages = [
                ...messagesWithoutLastAgent,
                {
                    ...lastRealAgentMessage,
                    content: "",
                    fullContent: lastRealAgentMessage.content,
                    isStreaming: true,
                },
            ];

            setChat({
                ...fullChat,
                messages: updatedMessages,
            });

            setIsStreaming(false);

            return true;
        } catch (e) {
            setIsStreaming(false);
            setIsCancelling(false);
            setError("Houve um erro ao enviar a mensagem.");
            return false;
        }
    };

    // AI-940: interrompe a geração em andamento.
    //  - Feedback IMEDIATO: setIsStreaming(false) -> o botão volta para "enviar" e o
    //    indicador de "processando" some na hora.
    //  - Avisa o backend (POST /chats/cancel): ele aborta a cadeia no próximo
    //    checkpoint e devolve o marcador "interrompida" (com id) na resposta do /send,
    //    que o handleSend renderiza com animação de digitação + botões de feedback.
    const handleStop = async () => {
        if (!isStreaming) return;
        setIsStreaming(false);
        setIsCancelling(true);
        const genId = generationIdRef.current;
        if (genId) {
            try { await service.cancel(genId); } catch { /* best-effort */ }
        }
    };

    const handleMe = useCallback(async () => {
        if (location.pathname.includes('/chats/list') || location.pathname.includes('/chats/tutorial')) return false;
        if (!location.pathname.includes('/chats')) return false;

        try {
            setIsVoting(false);
            setIsLoading(true);
            const [_Response, _Error] = await service.me(params);
            setIsLoading(false);

            if (!!_Error) return setError(_Response?.message || _Error);

            const data = _Response.data as ChatModel[];
            if (data.length > 0) {
                setChats(data);
                setChat(data.find((x) => x.status?.id === ChatsStatusEnum.EM_ANDAMENTO) || null);
                if (data.find((x) => x.status?.id === ChatsStatusEnum.EM_ANDAMENTO)) {
                    setState('started')
                } else {
                    setState('default')
                }
            } else {
                setChats([]);
                setChat(null);
                setState('default')
            }

            setError('');
        } catch (e) {
            setIsLoading(false);
            setError('Não foi possível carregar o chat.');
        }
    }, [params, location.pathname]);

    const handleList = useCallback(async () => {
        if (!location.pathname.includes('/chats/list')) return false;

        try {
            setIsLoading(true);
            const [_Response, _Error] = await service.list(params);
            setIsLoading(false);

            if (!!_Error) return setError(_Response?.message || _Error);

            setError('');
        } catch (e) {
            setIsLoading(false);
            setError('Não foi possível carregar os chats.');
        }
    }, [params, location.pathname]);

    const handleVote = async (messageId: number, vote: boolean) => {
        try {
            setIsLoadingVote(true);
            setIsVoting(true);

            const [_Response, _Error, _Code, _Errors] = await service.vote(messageId, vote);

            if (!!_Error) {
                ToastSettings(_Response?.message || _Error, "bottom-center", "error", () => { }, _Errors);
            }

            setChat((prev) =>
                prev ? {
                    ...prev,
                    messages: prev?.messages?.map(msg =>
                        msg.id === messageId
                            ? { ...msg, voted: true, vote }
                            : msg
                    ),
                }
                    : null
            );

            setIsLoadingVote(false);
        } catch (e) {
            setError("Houve um erro ao registrar seu voto.");
        }
    };

    return (
        <ChatsContext.Provider
            value={{
                chat,
                setChat,
                chats,
                message,
                setMessage,
                isStreaming,
                setIsStreaming,
                isCancelling,
                isLoadingVote,
                agentFinalText,
                isVoting,
                state,
                setState,
                handleSend,
                handleStop,
                handleList,
                handleMe,
                handleVote,
                showSimulation,
                setShowSimulation,
                activeAssistantId,
                setActiveAssistantId,
                isLoadingMessages,
                handleSelectAssistant,
            }}
        >
            {props.children}
        </ChatsContext.Provider>
    );
};


export const useChats = () => React.useContext(ChatsContext);
